'use client';

import { Button, Divider, Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useCookie } from 'react-use';

import { DomainInput } from '@/components/form/domainInput';
import { Link } from '@/components/shared/link';
import { accountDomainCookieName } from '@/contexts/environment';
import { useEnvironment } from '@/contexts/useEnvironment';

import { WelcomePageWrapper } from '../../welcomePageWrapper';

const isValidTenant = (tenant: string): boolean =>
  /^[a-zA-Z0-9-]+$/.test(tenant);

const redirectToTenant = (tenant: string, isDev: boolean) => {
  if (!isValidTenant(tenant)) {
    return;
  }
  const { protocol, host, search } = window.location;
  if (isDev) {
    const params = new URLSearchParams(search);
    params.set('domain', tenant);
    window.location.replace(`/login-v2?${params.toString()}`);
    return;
  }
  window.location.replace(`${protocol}//${tenant}.${host}/login-v2${search}`);
};

export const LoginV2DomainPage = () => {
  const { isDev, isTest } = useEnvironment();
  const [lastDomain] = useCookie(accountDomainCookieName);
  const [domain, setDomain] = useState('');
  const [focused, setFocused] = useState(true);

  useEffect(() => {
    if (lastDomain) {
      redirectToTenant(lastDomain, isDev);
      return;
    }
    if (isDev && !isTest) {
      redirectToTenant('eon-team', isDev);
    }
  }, [lastDomain, isDev, isTest]);

  const onLogin = (value: string) => {
    if (!value) {
      return;
    }
    redirectToTenant(value, isDev);
  };

  return (
    <WelcomePageWrapper>
      <Stack
        sx={{
          gap: '40px',
          overflow: 'hidden',
          width: '680px',
          maxWidth: '100%',
        }}
      >
        <Typography
          variant='h1'
          sx={{ maxWidth: '540px', wordWrap: 'break-word' }}
        >
          Log in with your account’s domain
        </Typography>
        <Stack direction='row' sx={{ alignItems: 'center', gap: '16px' }}>
          <DomainInput
            autoFocus
            focused={focused}
            onBlur={() => setFocused(false)}
            onFocus={() => setFocused(true)}
            slotProps={{ htmlInput: { 'data-testid': 'login-domain' } }}
            value={domain}
            sx={{ width: '308px' }}
            onChange={(e) => {
              const newValue = e.target.value;

              if (/^[a-zA-Z0-9-]*$/.test(newValue)) {
                setDomain(newValue);
              }
            }}
            onKeyUp={(event) => {
              if (event.key === 'Enter') {
                onLogin(domain);
              }
            }}
          />
          <Button
            className='h-full'
            disabled={!domain}
            data-testid='login-button'
            variant='contained'
            onClick={() => onLogin(domain)}
          >
            Log In
            <i className='material-symbols-arrow-right-alt ml-[8px] align-middle' />
          </Button>
        </Stack>
        <Divider
          sx={{
            borderStyle: 'dashed',
            borderColor: 'var(--mui-palette-divider)',
          }}
        />
        <Stack direction='row' sx={{ alignItems: 'center', gap: '8px' }}>
          <Typography>{`Don't have an account?`}</Typography>
          <Link
            className='font-bold no-underline'
            href='https://www.eon.io/get-a-demo'
            target='_blank'
          >
            Sign Up
          </Link>
        </Stack>
      </Stack>
    </WelcomePageWrapper>
  );
};
